import { apiRequest } from "./api";

export type TransportRoutePayload = {
    title: string;
    vehicle_id?: string | null;
    driver_id?: string | null;
    default_monthly_fee?: number;
    is_active?: boolean;
};

export type TransportRouteVehicle = {
    id: string;
    vehicle_number: string;
    model: string | null;
    seating_capacity: number;
};

export type TransportRouteDriver = {
    id: string;
    first_name: string;
    last_name: string;
};

export type TransportRouteRecord = {
    id: string;
    organization_id: string;
    title: string;
    vehicle_id: string | null;
    driver_id: string | null;
    default_monthly_fee?: string | number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    vehicle: TransportRouteVehicle | null;
    driver: TransportRouteDriver | null;
    students_count?: number;
};

export async function fetchTransportRoutes(): Promise<TransportRouteRecord[]> {
    const res = await apiRequest<TransportRouteRecord[]>('/api/transport-routes');
    return res.data;
}

export type RouteStopRecord = {
    id: string;
    organization_id: string;
    transport_route_id: string;
    transport_stop_id: string;
    order: number;
    pickup_time: string | null;
    drop_time: string | null;
    monthly_fee: string;
    created_at: string;
    updated_at: string;
    stop: {
        id: string;
        name: string;
        latitude: number | null;
        longitude: number | null;
    };
};

export async function fetchRouteStops(routeId: string): Promise<RouteStopRecord[]> {
    const res = await apiRequest<RouteStopRecord[]>(`/api/transport-routes/${routeId}/stops`);
    return res.data;
}

export type RouteStopPayload = {
    transport_stop_id: string;
    order: number;
    pickup_time?: string | null;
    drop_time?: string | null;
    monthly_fee?: number;
};

export async function addStopToRoute(routeId: string, payload: RouteStopPayload) {
    const res = await apiRequest(`/api/transport-routes/${routeId}/stops`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res;
}

export async function createTransportRoute(payload: TransportRoutePayload): Promise<TransportRouteRecord> {
    const res = await apiRequest<TransportRouteRecord>('/api/transport-routes', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function updateTransportRoute(routeId: string, payload: TransportRoutePayload): Promise<TransportRouteRecord> {
    const res = await apiRequest<TransportRouteRecord>(`/api/transport-routes/${routeId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function deleteTransportRoute(routeId: string): Promise<void> {
    await apiRequest(`/api/transport-routes/${routeId}`, { method: 'DELETE' });
}

// ── Route Manifest ───────────────────────────────────────────

export type RouteManifestStudent = {
    id: string;
    name: string;
    class_name: string | null;
    parent_phone: string | null;
};

export type RouteManifestApiStop = {
    id: string;
    order: number;
    pickup_time: string;
    drop_time: string;
    monthly_fee: string;
    stop: {
        id: string;
        name: string;
        latitude: number | null;
        longitude: number | null;
    };
    students: RouteManifestStudent[];
};

export type RouteManifestApiResponse = {
    id: string;
    title: string;
    is_active: boolean;
    vehicle: {
        id: string;
        vehicle_number: string;
        model: string | null;
        seating_capacity: number;
    } | null;
    driver: {
        id: string;
        first_name: string;
        last_name: string | null;
        phone: string | null;
    } | null;
    stops: RouteManifestApiStop[];
};

export async function fetchRouteManifest(routeId: string): Promise<RouteManifestApiResponse> {
    const res = await apiRequest<RouteManifestApiResponse>(`/api/reports/route-manifest/${routeId}`);
    return res.data;
}
